import React, { useState, useEffect, JSX } from 'react';
import {
    Box, AppBar, Toolbar, Typography, IconButton, Drawer,
    List, Divider, useMediaQuery, useTheme as useMuiTheme, Avatar, Tooltip
} from '@mui/material';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Notifications as NotificationsIcon,
    ExitToApp as LogoutIcon,
    Brightness4 as DarkModeIcon,
    Brightness7 as LightModeIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    TwoWheeler as MotosIcon,
    History as HistorialIcon,
    AccountBalanceWallet as BalancesIcon,
    Settings as ConfiguracionIcon,
    Person as PerfilIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../stores';
import { useTheme } from '../../../theme/ThemeContex';
import FullScreenLoader from '../../common/FullScreenLoader';

interface DashboardLayoutProps {
    children: React.ReactNode;
}

const TRANSITION_DURATION = 225;
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const menuSections = [
    {
        title: 'MENU',
        items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
            { text: 'Usuarios', icon: <PeopleIcon />, path: '/users' },
            { text: 'Conductores', icon: <PerfilIcon />, path: '/drivers' },
            { text: 'Motos', icon: <MotosIcon />, path: '/vehicles' },
            { text: 'Historial', icon: <HistorialIcon />, path: '/history' },
            { text: 'Balances', icon: <BalancesIcon />, path: '/balance' },
        ]
    },
    {
        title: 'OTROS',
        items: [
            { text: 'Perfil', icon: <PerfilIcon />, path: '/profile' },
            { text: 'Configuracion', icon: <ConfiguracionIcon />, path: '/settings' },
            { text: 'Cerrar sesión', icon: <LogoutIcon />, path: '/logout' },
        ]
    }
];

const DashboardLayout = observer(({ children }: DashboardLayoutProps) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { authStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const muiTheme = useMuiTheme();
    const { mode, toggleColorMode } = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const universalTransition = `all ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`;

    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        } else {
            setSidebarOpen(true);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobile) {
            setMobileOpen(false);
        }
    }, [location.pathname, isMobile]);

    const handleLogout = async () => {
        try {
            await authStore.logout();
            navigate('/login');

            setTimeout(() => {
                authStore.finishLogout();
            }, 300);
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
            navigate('/login');
            authStore.finishLogout();
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const toggleMobileDrawer = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuItemClick = (path: string) => {
        if (path === '/logout') {
            handleLogout();
        } else {
            navigate(path);
        }
    };

    const sidebarWidth = sidebarOpen ? 240 : 72;

    const renderMenuItem = (item: { text: string, icon: JSX.Element, path: string }, isCollapsed: boolean) => {
        const isActive = location.pathname === item.path;

        if (isCollapsed) {
            return (
                <Tooltip title={item.text} placement="right" key={item.text}>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(item.path)}
                        sx={{
                            justifyContent: 'center',
                            py: 1.5,
                            transition: universalTransition,
                            '&:hover': {
                                backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                                '& .MuiListItemIcon-root': {
                                    color: 'text.primary',
                                }
                            }
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 'auto',
                                color: isActive ? 'text.primary' : 'text.secondary',
                                transition: universalTransition,
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                    </ListItemButton>
                </Tooltip>
            );
        }

        return (
            <ListItemButton
                key={item.text}
                onClick={() => handleMenuItemClick(item.path)}
                sx={{
                    py: 1.5,
                    transition: universalTransition,
                    '&:hover': {
                        backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                        '& .MuiListItemIcon-root': {
                            color: 'text.primary',
                        },
                        '& .MuiTypography-root': {
                            color: 'text.primary',
                        }
                    }
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 40,
                        color: isActive ? 'text.primary' : 'text.secondary',
                        transition: universalTransition,
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.text}
                    sx={{
                        opacity: sidebarOpen ? 1 : 0,
                        transition: universalTransition,
                        '& .MuiTypography-root': {
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? 'text.primary' : 'text.secondary',
                        }
                    }}
                />
            </ListItemButton>
        );
    };

    const sidebarContent = (isTemporary = false) => (
        <Box sx={{
            height: '100%',
            backgroundColor: mode === 'light' ? '#ffffff' : '#121212',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: "flex-end",
                padding: muiTheme.spacing(2),
                minHeight: 73,
                transition: universalTransition,
            }}>
                <IconButton
                    onClick={isTemporary ? toggleMobileDrawer : toggleSidebar}
                    sx={{
                        transition: universalTransition,
                        '&:hover': {
                            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                        }
                    }}
                >
                    <ChevronLeftIcon
                        sx={{
                            transform: !sidebarOpen && !isTemporary ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: universalTransition,
                        }}
                    />
                </IconButton>
            </Box>
            <Divider />

            {authStore.user && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: muiTheme.spacing(2),
                    transition: universalTransition,
                }}>
                    <Avatar
                        sx={{
                            width: isTemporary || sidebarOpen ? 64 : 40,
                            height: isTemporary || sidebarOpen ? 64 : 40,
                            mb: isTemporary || sidebarOpen ? 1 : 0,
                            transition: universalTransition,
                            bgcolor: muiTheme.palette.primary.main,
                        }}
                    >
                        {authStore.user.username.charAt(0).toUpperCase()}
                    </Avatar>
                    {(isTemporary || sidebarOpen) && (
                        <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{
                                fontWeight: 'medium',
                                textAlign: 'center',
                                opacity: isTemporary || sidebarOpen ? 1 : 0,
                                maxHeight: isTemporary || sidebarOpen ? '50px' : '0',
                                overflow: 'hidden',
                                transition: universalTransition,
                            }}
                        >
                            {authStore.user.username}
                        </Typography>
                    )}
                </Box>
            )}
            <Divider />

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {menuSections.map((section, sectionIndex) => (
                    <React.Fragment key={section.title}>
                        {(isTemporary || sidebarOpen) && (
                            <Typography
                                variant="overline"
                                component="h3"
                                sx={{
                                    px: 3,
                                    pt: sectionIndex === 0 ? 2 : 3,
                                    pb: 1,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    letterSpacing: 1,
                                    opacity: isTemporary || sidebarOpen ? 1 : 0,
                                    height: isTemporary || sidebarOpen ? 'auto' : 0,
                                    overflow: 'hidden',
                                    transition: universalTransition,
                                }}
                            >
                                {section.title}
                            </Typography>
                        )}
                        <List sx={{ p: 0 }}>
                            {section.items.map(item => (
                                renderMenuItem(item, !isTemporary && !sidebarOpen)
                            ))}
                        </List>
                        {sectionIndex < menuSections.length - 1 && (
                            <Box sx={{ height: isTemporary || sidebarOpen ? 8 : 16 }} />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                height: '100vh',
                overflow: 'hidden',
            }}
            className={`layout-container ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}
        >
            {authStore.isLoggingOut && (
                <FullScreenLoader message="Cerrando sesión..." />
            )}

            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={toggleMobileDrawer}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': {
                        width: 240,
                        boxSizing: 'border-box',
                        backgroundColor: mode === 'light' ? '#ffffff' : '#121212',
                    },
                    zIndex: muiTheme.zIndex.drawer + 2,
                }}
            >
                {sidebarContent(true)}
            </Drawer>

            <Drawer
                variant="permanent"
                open={true}
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    width: sidebarWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: sidebarWidth,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                        border: 'none',
                        backgroundColor: mode === 'light' ? '#ffffff' : '#121212',
                        boxShadow: mode === 'light'
                            ? '0px 2px 4px -1px rgba(0,0,0,0.05)'
                            : '0px 2px 4px -1px rgba(0,0,0,0.15)',
                        transition: `${universalTransition}, background-color ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
                    },
                }}
            >
                {sidebarContent(false)}
            </Drawer>

            <Box
                component="main"
                className="main-content-container"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'hidden',
                    width: { xs: '100%', sm: `calc(100% - ${sidebarWidth}px)` },
                    transition: `width ${TRANSITION_DURATION}ms ${TRANSITION_EASING}, margin-left ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
                }}
            >
                <AppBar
                    position="static"
                    elevation={1}
                    sx={{
                        boxShadow: mode === 'light'
                            ? '0px 2px 4px -1px rgba(0,0,0,0.05)'
                            : '0px 2px 4px -1px rgba(0,0,0,0.15)',
                        zIndex: muiTheme.zIndex.drawer - 1,
                        width: '100%',
                        flexShrink: 0,
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: 56, sm: 73 } }}>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={toggleMobileDrawer}
                            sx={{ mr: 2, display: { sm: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Sistema de Gestión de Flota
                        </Typography>

                        <Tooltip title={mode === 'light' ? 'Modo oscuro' : 'Modo claro'}>
                            <IconButton
                                color="inherit"
                                onClick={toggleColorMode}
                                sx={{ mr: 1 }}
                            >
                                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                            </IconButton>
                        </Tooltip>

                        <IconButton color="inherit">
                            <NotificationsIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                <Box
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        flexGrow: 1,
                        overflow: 'auto',
                        height: '100%',
                        backgroundColor: 'background.default',
                        transition: `${universalTransition}, background-color ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
});

export default DashboardLayout;